import React, { useEffect, useMemo } from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useProjectApi from "hooks/api/private/useProjectApi";
import useProjectImageApi from "hooks/api/private/useProjectImageApi";
import { useProjectId } from "hooks/useId";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import useOrderableImages, { OrderableImagesHook } from "hooks/useOrderableImages";

import { DesktopMobileForm, ImagesDetails, LanguageEnum } from "types/api/commons";
import { ProjectBody, ProjectDetails } from "types/api/project";
import { DesktopOrMobile, NewsMediaEnum } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type ProjectsFormType = {
    id?: string;
    enabled: boolean;
    slug: string;
    place: string;
    category: string | null;
    cover_img_id?: string;
    cover_img: DesktopMobileForm;
    images: Array<ImagesDetails>;
    text: {
        [x in LanguageEnum.it]: {
            title: string;
            description: string;
        };
    };
};

export type ProjectsFormHook = {
    setDesktopMobileImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    orderableImagesHook: OrderableImagesHook;
    formHook: FormHookType<ProjectsFormType>;
};

function useProjectsForm(): ProjectsFormHook {
    const projectId = useProjectId();

    const { createProject, updateProject, getOneProject } = useProjectApi();
    const { createProjectImage, deleteProjectImage, updateProjectImage } = useProjectImageApi();

    const schema = useMemo<ObjectPropertyType>(
        () => ({
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                enabled: {
                    type: "simple",
                    validators: [],
                },
                slug: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                place: {
                    type: "simple",
                    validators: [],
                },
                category: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                cover_img_id: {
                    type: "simple",
                    validators: [],
                },
                cover_img: notEmptyMedia,
                images: {
                    type: "objectArray",
                    of: {
                        type: "object",
                        validators: [],
                        properties: {
                            id: {
                                type: "simple",
                                validators: [],
                            },
                            graphical_order: {
                                type: "simple",
                                validators: [],
                            },
                            media_id: {
                                type: "simple",
                                validators: [],
                            },
                            media: notEmptyMedia,
                        },
                    },
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            description: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        }),
        [],
    );

    const initialValues: ProjectsFormType = React.useMemo(
        () => ({
            enabled: true,
            url_video: "",
            slug: "",
            media: NewsMediaEnum.gallery,
            place: "",
            category: null,
            images: [],
            cover_img: {
                id: "",
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    description: "",
                },
            },
        }),
        [],
    );

    const formHook = useForm<ProjectsFormType>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const { setDesktopMobileImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "cover_img",
    });

    const [projectsDetails, setProjectsDetails] = React.useState<ProjectDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        if (projectId) {
            const [apiCall, abortController] = getOneProject({ id: projectId });

            apiCall.then(setProjectsDetails);

            return () => {
                abortController.abort();
            };
        }
    }, [getOneProject, projectId]);

    const orderableImagesHook = useOrderableImages({
        createImage: createProjectImage,
        updateImage: updateProjectImage,
        deleteImage: deleteProjectImage,
        fieldId: "project_id",
    });

    const {
        formHook: orderableImagesformHook,
        saveOrderableImages,
        initializeOrderableImage,
    } = orderableImagesHook;

    const {
        showAllError: showAllErrorOrderableImages,
        isValid: isValidOrderableImages,
        form: orderableImagesForm,
    } = orderableImagesformHook;

    // complete orderable images form from data
    React.useEffect(() => {
        if (projectsDetails) {
            initializeOrderableImage(projectsDetails.images);
        }
    }, [initializeOrderableImage, projectsDetails]);

    // complete form from data
    React.useEffect(() => {
        if (projectsDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.slug = projectsDetails.slug;
                newForm.enabled = projectsDetails.enabled;
                newForm.category = projectsDetails.category;
                newForm.id = projectsDetails.id;
                newForm.place = projectsDetails.place;

                newForm.text.it.title = projectsDetails.text.it.title;
                newForm.text.it.description = projectsDetails.text.it.description;

                newForm.cover_img.id = projectsDetails.cover_img.id;
                newForm.cover_img = {
                    id: projectsDetails.cover_img.id,
                    desktop: createAttachmentType({
                        id: projectsDetails.cover_img.desktop.id ?? null,
                        label: "Desktop",
                        filename: projectsDetails.cover_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: projectsDetails.cover_img.mobile.id ?? null,
                        label: "Mobile",
                        filename: projectsDetails.cover_img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, projectsDetails]);

    const { addSaveAction } = useSaveActionsContext();

    useEffect(() => {
        addSaveAction("useProjectsForm", () => {
            if (isValid && form.category && isValidOrderableImages) {
                const promises: Promise<unknown>[] = [];

                const body: ProjectBody = {
                    enabled: form.enabled,
                    slug: form.slug,
                    place: form.place,
                    category: form.category,
                    cover_img: {
                        id: form.cover_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.cover_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.cover_img.mobile),
                    },
                    text: {
                        it: {
                            title: form.text.it.title,
                            description: form.text.it.description,
                        },
                    },
                };

                if (projectId) {
                    const [apiCall] = updateProject({ id: projectId }, body);

                    promises.push(apiCall, saveOrderableImages(projectId));
                } else {
                    promises.push(
                        new Promise((resolve, reject) => {
                            const [apiCall] = createProject(body);

                            apiCall
                                .then((response) => {
                                    if (orderableImagesForm.images.length > 0) {
                                        saveOrderableImages(response.id)
                                            .then(resolve)
                                            .catch(reject);
                                    } else {
                                        resolve(response);
                                    }
                                })
                                .catch(reject);
                        }),
                    );
                }

                return Promise.all(promises);
            } else {
                showAllErrorOrderableImages();
                showAllError();

                return Promise.reject(new Error("INVALID_FORM"));
            }
        });
    }, [
        showAllErrorOrderableImages,
        saveOrderableImages,
        createProject,
        addSaveAction,
        updateProject,
        showAllError,
        orderableImagesForm.images.length,
        isValidOrderableImages,
        projectId,
        isValid,
        form,
    ]);

    return React.useMemo(
        (): ProjectsFormHook => ({
            setDesktopMobileImage,
            orderableImagesHook,
            formHook,
        }),
        [setDesktopMobileImage, orderableImagesHook, formHook],
    );
}

export default useProjectsForm;
